/**
 * Converts Firebase Authentication error codes to user-friendly messages
 * @param errorCode - Firebase error code (e.g., 'auth/user-not-found')
 * @returns User-friendly error message
 */
export function getAuthErrorMessage(errorCode: string): string {
  const errorMessages: { [key: string]: string } = {
    // Email/Password Sign In Errors
    'auth/user-not-found': 'No account found with this email address. Please sign up first.',
    'auth/wrong-password': 'Incorrect password. Please try again or reset your password.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled. Please contact support for assistance.',
    'auth/invalid-credential': 'Invalid email or password. Please check your credentials and try again.',
    
    // Registration Errors
    'auth/email-already-in-use': 'An account with this email already exists. Please login instead.',
    'auth/weak-password': 'Password is too weak. Please use at least 6 characters with a mix of letters and numbers.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
    
    // Network & Connection Errors
    'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
    'auth/too-many-requests': 'Too many unsuccessful attempts. Please wait a few minutes and try again.',
    'auth/timeout': 'Request timed out. Please check your connection and try again.',
    
    // Google Sign-In Errors
    'auth/popup-closed-by-user': 'Sign-in cancelled. Please try again if you want to continue.',
    'auth/popup-blocked': 'Pop-up was blocked by your browser. Please allow pop-ups for this site.',
    'auth/cancelled-popup-request': 'Sign-in cancelled. Only one sign-in request at a time.',
    'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method. Try signing in with email/password or another provider.',
    
    // Token & Session Errors
    'auth/requires-recent-login': 'For security, please log out and log back in before trying this action.',
    'auth/expired-action-code': 'This link has expired. Please request a new one.',
    'auth/invalid-action-code': 'This link is invalid or has already been used.',
    
    // Multi-factor Authentication
    'auth/multi-factor-auth-required': 'Please complete the additional authentication step.',
    'auth/maximum-second-factor-count-exceeded': 'Maximum number of second factors exceeded.',
    
    // Missing Information
    'auth/missing-email': 'Please provide an email address.',
    'auth/missing-password': 'Please provide a password.',
    'auth/missing-phone-number': 'Please provide a phone number.',
    
    // Other Common Errors
    'auth/internal-error': 'An unexpected error occurred. Please try again.',
    'auth/invalid-api-key': 'Configuration error. Please contact support.',
    'auth/app-not-authorized': 'This app is not authorized. Please contact support.',
    'auth/unauthorized-domain': 'This domain is not authorized. Please contact support.',
    'auth/credential-already-in-use': 'This credential is already associated with a different account.',
    
    // Password Reset Errors
    'auth/invalid-password': 'Invalid password. Passwords must be at least 6 characters.',
    'auth/user-token-expired': 'Your session has expired. Please log in again.',
  };

  // Check if we have a custom message for this error code
  if (errorMessages[errorCode]) {
    return errorMessages[errorCode];
  }

  // If error code contains 'auth/', remove it for cleaner display
  if (errorCode.includes('auth/')) {
    const readableError = errorCode
      .replace('auth/', '')
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return `${readableError}. Please try again or contact support if the problem persists.`;
  }

  // Default fallback message
  return 'An error occurred. Please try again or contact support if the problem persists.';
}

/**
 * Extracts the error code from a Firebase error object
 * @param error - Error object from Firebase
 * @returns Firebase error code or generic code
 */
export function extractErrorCode(error: any): string {
  if (error?.code) {
    return error.code;
  }
  
  if (error?.message && error.message.includes('auth/')) {
    const match = error.message.match(/auth\/[\w-]+/);
    return match ? match[0] : 'unknown-error';
  }
  
  return 'unknown-error';
}

/**
 * Gets a user-friendly error message from a Firebase error object
 * @param error - Error object from Firebase
 * @returns User-friendly error message
 */
export function getFriendlyAuthError(error: any): string {
  const errorCode = extractErrorCode(error);
  return getAuthErrorMessage(errorCode);
}
